import { motion } from "framer-motion";
import {
  MonitorSmartphone,
  Palette,
  Megaphone,
  ShieldCheck,
  ArrowUpRight,
} from "lucide-react";
import Reveal from "./Reveal.jsx";

const SERVICES = [
  {
    icon: MonitorSmartphone,
    title: "Hemsida",
    desc: "Snabba, responsiva och konverteringsoptimerade webbplatser byggda i React, med modern animation och SEO i grunden.",
    features: ["React + Vite", "SEO & Core Web Vitals", "CMS efter behov"],
    accent: "from-navy-700 to-navy-900",
  },
  {
    icon: Palette,
    title: "Grafisk formgivning",
    desc: "Visuell identitet, logotyp och designsystem som skapar igenkänning i varje touchpoint.",
    features: ["Logotyp & identitet", "Designsystem", "Tryck & digitalt"],
    accent: "from-sand-500 to-sand-700",
  },
  {
    icon: Megaphone,
    title: "Sociala Medier",
    desc: "Strategi, innehållsproduktion och annonsering för Instagram, LinkedIn och Meta — kopplat till mätbara resultat.",
    features: ["Strategi & content", "Annonser & analytics", "Månadsrapport"],
    accent: "from-leather to-sand-700",
  },
  {
    icon: ShieldCheck,
    title: "Säkerhet",
    desc: "GDPR-anpassad infrastruktur, säkerhetsuppdateringar och övervakning så att du kan sova lugnt om natten.",
    features: ["SSL & härdning", "Backup & övervakning", "GDPR-rådgivning"],
    accent: "from-navy-800 to-ink",
  },
];

export default function Services() {
  return (
    <section id="tjanster" className="relative bg-white py-24 sm:py-32">
      <div className="container-x">
        <div className="mx-auto max-w-2xl text-center">
          <Reveal>
            <span className="eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-sand-500" />
              Våra tjänster
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="section-title mt-5">
              Allt du behöver för att synas{" "}
              <span className="text-leather">på riktigt.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-lg leading-relaxed text-navy-700">
              Från första skiss till lansering och löpande drift — vi tar
              helhetsansvar för din digitala närvaro.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.title} delay={0.06 * i}>
                <motion.article
                  whileHover={{ y: -6 }}
                  transition={{ type: "spring", stiffness: 220, damping: 18 }}
                  className="group relative h-full overflow-hidden rounded-3xl border border-navy-900/8 bg-white p-7 shadow-soft"
                >
                  {/* Accent gradient on hover */}
                  <div
                    className={`pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br ${s.accent} opacity-0 transition duration-500 group-hover:opacity-[0.96]`}
                  />
                  <div
                    className={`grid h-12 w-12 place-items-center rounded-2xl bg-sand-100 text-navy-800 transition group-hover:bg-white/15 group-hover:text-white`}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-semibold text-navy-900 transition group-hover:text-white">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-navy-700 transition group-hover:text-white/85">
                    {s.desc}
                  </p>
                  <ul className="mt-5 space-y-1.5 text-sm text-navy-600 transition group-hover:text-white/80">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-center gap-2">
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-sand-500 group-hover:bg-sand-300" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7 flex items-center gap-1 text-sm font-semibold text-navy-900 transition group-hover:text-white">
                    Läs mer
                    <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </motion.article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
